import { GeneratedAnswerInterface, APIOutput } from "@amzn/ask-legal-domain";
import { Alert, Box, Button, ExpandableSection, Header, Icon, Link, SpaceBetween } from "@amzn/awsui-components-react";
import * as React from "react";
import { useSearchStreaming } from "../../../hooks/useSearchStreaming";
import { AppContext } from "../../../setup/context";
import "../../../styles/component/search/SearchStreamingContainer.scss";

interface SearchStreamingContainerProps {
    request: GeneratedAnswerInterface.GeneratedAnswerRequest | null;
    shouldStart: boolean;
}

export const SearchStreamingContainer: React.FC<SearchStreamingContainerProps> = ({
    request,
    shouldStart
}) => {
    const context = React.useContext(AppContext);
    const {
        isLoading,
        content,
        citations,
        metadata,
        error,
        isComplete,
        thinkingMessage,
        generateRealStreamingAnswer,
        reset
    } = useSearchStreaming();

    const [isExpanded, setIsExpanded] = React.useState(true);
    const [isResponseVisible, setIsResponseVisible] = React.useState(false);
    const [selectedFeedback, setSelectedFeedback] = React.useState<GeneratedAnswerInterface.FeedbackRating | null>(null);
    const [sourcesExpanded, setSourcesExpanded] = React.useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);
    const [submittingFeedback, setSubmittingFeedback] = React.useState<GeneratedAnswerInterface.FeedbackRating | null>(null);
    const [feedbackError, setFeedbackError] = React.useState<string | null>(null);

    // Start real streaming when shouldStart becomes true and we have a request
    React.useEffect(() => {
        if (shouldStart && request && !isLoading && !error) {
            generateRealStreamingAnswer(request);
        }
    }, [shouldStart, request, generateRealStreamingAnswer]);

    // Reset when request changes
    React.useEffect(() => {
        if (request) {
            reset();
            setIsResponseVisible(false);
        }
    }, [request, reset]);

    const renderSummaryContainer = (content: React.ReactNode, includeExpandControl = false) => (
        <ExpandableSection
            defaultExpanded={true}
            variant="container"
            headerText={
                <span>
                    <Icon name="gen-ai" size="medium" /> AI-Generated Summary <span className="beta-label">[Beta]</span>
                </span>
            }
            {...(includeExpandControl && {
                expanded: isExpanded,
                onChange: ({ detail }) => setIsExpanded(detail.expanded)
            })}
        >
            {content}
        </ExpandableSection>
    );

    if (error) {
        return renderSummaryContainer(
            <Alert type="error" header="Failed to generate search summary">
                {error}
            </Alert>
        );
    }

    // Show container immediately
    if (!content && !isLoading && !thinkingMessage && !shouldStart) {
        return null;
    }

    const handleFeedback = async (feedback: GeneratedAnswerInterface.FeedbackRating) => {
        if (!content || feedbackSubmitted || submittingFeedback) {
            return;
        }

        setSelectedFeedback(feedback);
        setSubmittingFeedback(feedback);
        setFeedbackError(null);

        try {
            const feedbackRequest = GeneratedAnswerInterface.SubmitFeedbackRequest.create({
                instanceId: request?.instanceId || "",
                feedback: feedback,
                question: request?.query || "",
                generatedAnswer: content
            });

            const response = await context.getGeneratedAnswerAPI().submitFeedback(feedbackRequest);
            const output = APIOutput.fromRaw<GeneratedAnswerInterface.SubmitFeedbackResponse>(response.data);

            if (output.isErr()) {
                setFeedbackError(output.err.message);
            } else {
                setFeedbackSubmitted(true);
            }
        } catch (error) {
            console.error("Failed to submit feedback:", error);
            setFeedbackError(error instanceof Error ? error.message : "Failed to submit feedback");
        } finally {
            setSubmittingFeedback(null);
        }
    };

    return (
        <div className="search-streaming-container">
            <ExpandableSection
                defaultExpanded={true}
                variant="container"
                headerText={
                    <span>
                        <Icon name="gen-ai" size="medium" /> AI-Generated Summary <span className="beta-label">[Beta]</span>
                    </span>
                }
                onChange={({ detail }) => setIsExpanded(detail.expanded)}
            >
                {isResponseVisible && <Alert type="info" header="AI-Generated Content">
                    AI-generated content may not always be accurate and can produce inaccurate, biased, or inconsistent outputs. Users are advised to exercise their own judgment and not solely depend on the outputs of this system. Where AI-generated responses conflict with official company policies, legal requirements, or authoritative sources, those official sources take priority.
                </Alert>}

            {/* Render thinking message */}
            {!content && (thinkingMessage || shouldStart) ? (
                /* Show thinking message with GenUI-style background when thinking and no content yet */
                <div className="thinking-container">
                    <div className="thinking-shimmer-overlay" />

                    <div className="thinking-content-wrapper">
                        {/* AI Icon */}
                        <div className="ai-icon">
                            <Icon name="gen-ai" size="medium" variant="inverted" />
                        </div>

                        <div className="thinking-content">
                            <div className="thinking-message">
                                {thinkingMessage || "Analyzing your search query"}
                            </div>
                            {/* Processing dots */}
                            <div className="processing-dots-container">
                                <div className="thinking-dots">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                    <Box padding={{ top: "xs" }}>
                        {/* Show content with show/hide functionality */}
                        <div className={`content-container ${!isResponseVisible ? "collapsed" : ""}`}>
                            <div
                                dangerouslySetInnerHTML={{ __html: content }}
                            />
                            {isLoading && (
                                <div className="loading-indicator">
                                    <span className="loading-text">
                                        Almost there
                                    </span>
                                    <div className="thinking-dots">
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                    </div>
                                </div>
                            )}
                            {!isResponseVisible && content && (
                                <div className="fade-overlay" />
                            )}
                        </div>
                    </Box>
                )}

                {isResponseVisible && isComplete && (
                    <>
                        {/* Citations */}
                        {citations && citations.length > 0 && (
                            <Box padding={{ top: "xs" }}>
                                <ExpandableSection
                                    headerText={`Sources (${citations.length})`}
                                    expanded={sourcesExpanded}
                                    onChange={({ detail }) => setSourcesExpanded(detail.expanded)}
                                >
                                    <Box padding={{ top: "xs" }}>
                                        {citations.map((citation) => (
                                            <Box key={citation.id} padding={{ vertical: "xxs" }}>
                                                <Box variant="small">
                                                    [{citation.id}] <Link href={citation.url} external>{citation.title}</Link>
                                                </Box>
                                                <Box variant="small" color="text-body-secondary">
                                                    {citation.excerpt}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </ExpandableSection>
                            </Box>
                        )}

                        {/* Feedback Section */}
                        <Box padding={{ top: "s" }}>
                            {feedbackError && (
                                <Box padding={{ bottom: "xs" }}>
                                    <Alert type="error" dismissible onDismiss={() => setFeedbackError(null)}>
                                        {feedbackError}
                                    </Alert>
                                </Box>
                            )}
                            {!feedbackSubmitted ? (
                                <>
                                    <Box variant="small" color="text-body-secondary" padding={{ bottom: "xs" }}>
                                        Rate this response
                                    </Box>
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button
                                            variant={selectedFeedback === GeneratedAnswerInterface.FeedbackRating.HELPFUL ? "primary" : "normal"}
                                            size="small"
                                            iconName="thumbs-up"
                                            onClick={() => handleFeedback(GeneratedAnswerInterface.FeedbackRating.HELPFUL)}
                                            disabled={submittingFeedback !== null}
                                            loading={submittingFeedback === GeneratedAnswerInterface.FeedbackRating.HELPFUL}
                                        >
                                            Helpful
                                        </Button>
                                        <Button
                                            variant={selectedFeedback === GeneratedAnswerInterface.FeedbackRating.SOMEWHAT_HELPFUL ? "primary" : "normal"}
                                            size="small"
                                            iconName="status-info"
                                            onClick={() => handleFeedback(GeneratedAnswerInterface.FeedbackRating.SOMEWHAT_HELPFUL)}
                                            disabled={submittingFeedback !== null}
                                            loading={submittingFeedback === GeneratedAnswerInterface.FeedbackRating.SOMEWHAT_HELPFUL}
                                        >
                                            Somewhat helpful
                                        </Button>
                                        <Button
                                            variant={selectedFeedback === GeneratedAnswerInterface.FeedbackRating.UNHELPFUL ? "primary" : "normal"}
                                            size="small"
                                            iconName="thumbs-down"
                                            onClick={() => handleFeedback(GeneratedAnswerInterface.FeedbackRating.UNHELPFUL)}
                                            disabled={submittingFeedback !== null}
                                            loading={submittingFeedback === GeneratedAnswerInterface.FeedbackRating.UNHELPFUL}
                                        >
                                            Unhelpful
                                        </Button>
                                    </SpaceBetween>
                                </>
                            ) : (
                                <Alert type="success" dismissible={false}>
                                    Thank you for your feedback!
                                </Alert>
                            )}
                        </Box>

                        {/* Metadata */}
                        {metadata && (
                            <Box padding={{ top: "s" }} variant="small" color="text-body-secondary">
                                {new Date(metadata.timestamp).toLocaleString()} • Based on {metadata.resultCount} results
                                <br />
                                Usage is subject to <Link href="https://aws.amazon.com/ai/responsible-ai/policy/" external>AWS Responsible AI Policy</Link>
                            </Box>
                        )}
                    </>
                )}

                {/* Show/Hide Response Button - Footer */}
                {content && (
                    <Box padding={{ top: "s" }}>
                        <Button
                            variant="primary"
                            iconName={isResponseVisible ? "angle-up" : "angle-down"}
                            onClick={() => setIsResponseVisible(!isResponseVisible)}
                        >
                            {isResponseVisible ? "Hide response" : "Show response"}
                        </Button>
                    </Box>
                )}
            </ExpandableSection>
        </div>
    );
};
